import { useQuery } from '@tanstack/react-query';
import { EmptyState, ListGroup, ListRow, SegmentedControl, Skeleton } from '@uplink/ui';
import { useState } from 'react';
import { errorText, useTRPC } from '../api.ts';
import type { Nav } from '../nav.ts';
import { usePrefs } from '../prefs.tsx';
import {
  AmountStatus,
  FilterChips,
  ORDER_KIND,
  ORDER_STATUS,
  Pager,
  providerName,
  TOPUP_STATUS,
} from './common.tsx';

type View = 'orders' | 'topups';
type OrderFilter = 'all' | 'attention' | 'active' | 'provisioning' | 'refunded';
type KindFilter = 'all' | 'domain' | 'server';
type TopupFilter = 'all' | 'paid' | 'pending' | 'expired';

const ORDER_FILTERS: { value: OrderFilter; label: string }[] = [
  { value: 'all', label: 'Все' },
  { value: 'attention', label: 'Требуют внимания' },
  { value: 'active', label: 'Выполнены' },
  { value: 'provisioning', label: 'В работе' },
  { value: 'refunded', label: 'Возвраты' },
];
const KIND_FILTERS: { value: KindFilter; label: string }[] = [
  { value: 'all', label: 'Домены и серверы' },
  { value: 'domain', label: 'Домены' },
  { value: 'server', label: 'Серверы' },
];
const TOPUP_FILTERS: { value: TopupFilter; label: string }[] = [
  { value: 'all', label: 'Все' },
  { value: 'paid', label: 'Оплачены' },
  { value: 'pending', label: 'Ждут оплаты' },
  { value: 'expired', label: 'Истекли' },
];

const fullName = (u: { firstName: string; lastName: string | null }) =>
  [u.firstName, u.lastName].filter(Boolean).join(' ');

export function AdminOrdersScreen({ nav, filter }: { nav: Nav; filter?: 'attention' }) {
  const [view, setView] = useState<View>('orders');
  return (
    <main className="ul-screen">
      <h1 className="ul-screen__title">{view === 'orders' ? 'Заказы' : 'Пополнения'}</h1>
      <SegmentedControl<View>
        label="Раздел"
        value={view}
        onChange={setView}
        options={[
          { value: 'orders', label: 'Заказы' },
          { value: 'topups', label: 'Пополнения' },
        ]}
      />
      {view === 'orders' ? <Orders nav={nav} initial={filter ?? 'all'} /> : <Topups nav={nav} />}
    </main>
  );
}

function Orders({ nav, initial }: { nav: Nav; initial: OrderFilter }) {
  const trpc = useTRPC();
  const { usd, dateTime } = usePrefs();
  const [status, setStatus] = useState<OrderFilter>(initial);
  const [kind, setKind] = useState<KindFilter>('all');
  const [offset, setOffset] = useState(0);
  const orders = useQuery({
    ...trpc.admin.orders.queryOptions({
      status: status === 'all' ? undefined : status,
      kind: kind === 'all' ? undefined : kind,
      offset,
    }),
    placeholderData: (prev) => prev,
  });

  return (
    <>
      <FilterChips
        options={ORDER_FILTERS}
        value={status}
        onChange={(v) => {
          setStatus(v);
          setOffset(0);
        }}
      />
      <FilterChips
        options={KIND_FILTERS}
        value={kind}
        onChange={(v) => {
          setKind(v);
          setOffset(0);
        }}
      />
      {orders.isPending ? (
        <Skeleton height={320} />
      ) : orders.isError ? (
        <div className="ul-error" role="alert">
          {errorText(orders.error)}
        </div>
      ) : orders.data.rows.length === 0 ? (
        <EmptyState icon="receipt" title="Заказов нет" text="С такими фильтрами ничего не нашлось" />
      ) : (
        <div className={orders.isPlaceholderData ? 'ul-results ul-results--stale' : 'ul-results'}>
          <div className="ul-multiline">
            <ListGroup header={`Найдено ${orders.data.total}`}>
              {orders.data.rows.map((o) => (
                <ListRow
                  key={o.id}
                  icon={o.kind.startsWith('server') ? 'server' : 'globe'}
                  tone={o.status === 'failed' ? 'crit' : o.status === 'manual_review' ? 'warn' : undefined}
                  title={o.subject ?? ORDER_KIND[o.kind] ?? o.kind}
                  subtitle={[
                    `${ORDER_KIND[o.kind] ?? o.kind} · ${fullName(o.user)}`,
                    o.provider
                      ? `${providerName(o.provider)} · тариф ${o.planId} · upstream ${o.upstreamPlanId ?? '—'}`
                      : null,
                    `${dateTime(o.createdAt)} · маржа ${usd(o.amountCents - o.costCents)}`,
                    o.error ? `Ошибка: ${o.error}` : null,
                  ]
                    .filter(Boolean)
                    .join('\n')}
                  trailing={<AmountStatus amount={usd(o.amountCents)} map={ORDER_STATUS} status={o.status} />}
                  onClick={() => nav.push({ name: 'adminUser', id: o.user.id })}
                />
              ))}
            </ListGroup>
          </div>
          <Pager offset={offset} total={orders.data.total} onChange={setOffset} />
        </div>
      )}
    </>
  );
}

function Topups({ nav }: { nav: Nav }) {
  const trpc = useTRPC();
  const { usd, dateTime } = usePrefs();
  const [status, setStatus] = useState<TopupFilter>('paid');
  const [offset, setOffset] = useState(0);
  const topups = useQuery({
    ...trpc.admin.topups.queryOptions({ status: status === 'all' ? undefined : status, offset }),
    placeholderData: (prev) => prev,
  });

  return (
    <>
      <FilterChips
        options={TOPUP_FILTERS}
        value={status}
        onChange={(v) => {
          setStatus(v);
          setOffset(0);
        }}
      />
      {topups.isPending ? (
        <Skeleton height={320} />
      ) : topups.isError ? (
        <div className="ul-error" role="alert">
          {errorText(topups.error)}
        </div>
      ) : topups.data.rows.length === 0 ? (
        <EmptyState icon="wallet" title="Пополнений нет" />
      ) : (
        <div className={topups.isPlaceholderData ? 'ul-results ul-results--stale' : 'ul-results'}>
          <ListGroup header={`Найдено ${topups.data.total}`}>
            {topups.data.rows.map((t) => (
              <ListRow
                key={t.id}
                icon="wallet"
                title={fullName(t.user)}
                subtitle={[
                  dateTime(t.paidAt ?? t.createdAt),
                  t.paidAmount && t.paidAsset ? `${t.paidAmount} ${t.paidAsset}` : null,
                ]
                  .filter(Boolean)
                  .join(' · ')}
                trailing={<AmountStatus amount={usd(t.amountCents)} map={TOPUP_STATUS} status={t.status} />}
                onClick={() => nav.push({ name: 'adminUser', id: t.user.id })}
              />
            ))}
          </ListGroup>
          <Pager offset={offset} total={topups.data.total} onChange={setOffset} />
        </div>
      )}
    </>
  );
}
